import { toValue } from '../modules/vueuse/toValue';
import { useTraits } from '../../index';
import { fireLinkTag } from '../analytics/tagManager';
import { usePrice } from '../modules/price/components';
import { usePrice as usePriceBcom } from '../modules/price/components/bcom';
import { useUpc } from './useUpc/useUpc';

/**
 * Gets the availability message based on the provided UPC data.
 *
 * @param {Pick<upcData, 'seller' | 'marketplaceFlag'> &
 * {availability: Pick<upcData['availability'], 'available' | 'lowAvailability' | 'shipDays' | 'availabilityMessage'> }
 * | undefined} selectedUpcData - The UPC data representing the specific combination of product features.
 * @param {boolean} marketPlaceEnabled - Indicates whether the marketplace is enabled.
 * @returns {string[]} - An array of availability messages.
 *
 */
export function _getAvailabilityMessage(selectedUpcData, marketPlaceEnabled) {
  if (!selectedUpcData) return [''];
  const { seller, marketplaceFlag, availability } = selectedUpcData;
  if (marketPlaceEnabled && marketplaceFlag && seller?.sellerName) {
    if (availability.available) {
      return availability.lowAvailability ? [`just a few left: shipped by marketplace in ${availability.shipDays} days`] : [`Shipped by marketplace in ${availability.shipDays} days`];
    }
    return ['this product is currently unavailable'];
  }
  return [availability.availabilityMessage];
}

/**
 * Gets the holiday shipping and pickup messages based on the provided UPC data.
 *
 * @param {Pick<upcData, 'holidayMessageEligible' | 'holidayMessages'> &
 * {availability: Pick<upcData['availability'], 'holidayMessages'> }
 * | undefined} selectedUpcData - The UPC data representing the specific combination of product features.
 * @returns {string} - The holiday shipping and pickup messages.
 */
export function _getHolidayMessage(selectedUpcData) {
  if (!selectedUpcData) return '';
  const holidayMessageEligible = selectedUpcData?.holidayMessageEligible || false; // TODO refactor
  if (holidayMessageEligible) {
    const holidayShipMessage = selectedUpcData?.holidayMessages.beforeStandardCutoff || false;
    const holidayPickupMessage = selectedUpcData?.availability.holidayMessages.beforeBossCutoff;
    return `${holidayShipMessage ? 'Shipping:BH' : 'Shipping:AH'}|${holidayPickupMessage ? 'Pickup:BH' : `${(holidayPickupMessage !== undefined) ? 'Pickup:AH' : 'PickUp:NA'}`}`;
  }
  return `${'Shipping:NA'} | ${'Pickup:NA'}`;
}

/**
 * Retrieves data related to new arrivals and markdowns based on flags and price type text.
 *
 * @param {boolean} isNewFlag - A flag indicating whether the product is a new arrival.
 * @param {string} [priceTypeText] - The text indicating the price type, e.g., 'new markdown'.
 * @returns {{
*   product_badge_new_arrival: string[],
*   product_badge_new_markdown: string[]
* }|{}}
*/
export function _getNewArrivalAndMarkdownData(isNewFlag, priceTypeText) {
  if (!priceTypeText) return {};
  const NEW_ARRIVAL = 'new arrival';
  const NEW_MARKDOWN = 'new markdown';

  const priceTypeTextLower = priceTypeText.toLowerCase();

  return {
    product_badge_new_arrival: isNewFlag ? [NEW_ARRIVAL] : [''],
    product_badge_new_markdown: priceTypeTextLower.indexOf(NEW_MARKDOWN) > -1 ? [priceTypeTextLower] : [''],
  };
}

export function _getConsolidatedMasterData({ isConsolidatedMaster }) {
  return toValue(isConsolidatedMaster)
    ? { product_type: ['member'] }
    : {};
}

export function _getSddEligibilityData({ selectedUpcData = {}, selectedSddData = {} }) {
  const { availability = {}, identifier = {} } = selectedUpcData;
  const ssdAvailability = selectedSddData?.[+identifier?.upcNumber]?.ssdAvailability;

  return {
    sdd_eligibility_flag: (availability.available && ssdAvailability)
      ? ['true']
      : ['false'],
  };
}

/**
 * Retrieves link name related to splitting/consolidated third variant size and type selection
 *
 * @param {Object} options - Options for third variant link data.
 * @param {boolean | undefined} options.isSplitThirdVariant - A flag indicating whether the product is splitting third variant.
 * @param {boolean | undefined} options.isConsolidatedThirdVariant - A flag indicating whether the product is consolidated third variant.
 * @param {boolean | undefined} [options.isVariant] - Parameter to identify if trigger is size or orientation
 * @returns {{link_name: string}|{}}
*/

export function _getThirdVariantLinkData({
  isSplitThirdVariant,
  isConsolidatedThirdVariant,
  isVariant,
}) {
  if (!isSplitThirdVariant && !isConsolidatedThirdVariant) {
    return {};
  }
  return {
    link_name: isVariant ? 'pdp_size_variant_clickable' : 'pdp_size_clickable',
  };
}

/**
 * Handles analytics events for product trait selection.
 *
 * @param {Object} options - Options for the analytics data.
 * @param {import('@vueuse/core').MaybeRef<boolean>} options.isConsolidatedMaster
 * @param {import('@vueuse/core').MaybeRef<boolean>} options.isMcom
 * @param {import('@vueuse/core').MaybeRef<object>} options.product - The raw product
 * @param {boolean | undefined} [options.isSplitThirdVariant]
 * @param {boolean | undefined} [options.isConsolidatedThirdVariant]
 * @param {boolean | undefined} [options.enhanceSameDayDeliveryEnabled]
 *
 */
// eslint-disable-next-line complexity
export function useTraitAnalyticsData({
  isConsolidatedMaster,
  isMcom,
  product,
  isSplitThirdVariant,
  isConsolidatedThirdVariant,
}) {
  const isConsolidatedMasterValue = toValue(isConsolidatedMaster);
  const isMcomValue = toValue(isMcom);
  const productValue = toValue(product);
  const productId = String(productValue.id);
  const { selectedColor, selectedSize } = useTraits(productId);
  const { selectedUpcId } = useUpc(productId);

  const priceData = {
    id: productId,
    selectedColor,
    selectedSize,
    selectedUpc: selectedUpcId,
  };
  const { selectedPrice } = isMcom ? usePrice(priceData) : usePriceBcom(priceData);

  const {
    isMarketPlaceEligible: isMarketPlaceEligibleFlag,
    isNew: isNewFlag,
  } = productValue.detail?.flags;

  const fireTagOnChange = ({
    extraData = {}, isVariant, isColorChage, productPricingState, selectedSddData,
  }) => {
    const selectedUpcData = productValue?.relationships?.upcs?.[selectedUpcId.value];
    const selectedUpcNumber = selectedUpcData?.identifier?.upcNumber;

    // utag data shared between both brands
    const baseUtagData = {
      event_name: 'product select sku',
      link_name: isColorChage ? 'pdp_color_clickable_swatch' : 'pdp_size_clickable',
      holiday_eligibility: [_getHolidayMessage(selectedUpcData)],
      pdd_availability_message: '', // TODO: To be done after store selection is implemented
      pdd_eligibility_flag: '', // TODO: To be done after store selection is implemented
      product_availability_message: _getAvailabilityMessage(selectedUpcData, isMarketPlaceEligibleFlag),
      product_id: [productId],
      product_low_availability: [`${selectedUpcData?.availability?.lowAvailability || ''}`],
      product_price: [`${selectedPrice?.value?.tieredPrice?.[0]?.values?.[0].value || ''}`],
      product_pricing_state: productPricingState,
      product_upc: [`${selectedUpcNumber || selectedUpcId.value || ''}`],
    };

    // MCOM only
    // TODO: Implement pickup_eligibility once store selection is implemented
    const mcomUtagData = {
      delivery_type: selectedUpcData?.availability?.orderType === 'DROP' ? 'vendor direct' : 'own',
      product_promo_code_flag: [selectedPrice.value?.flags.isWithOfferPromo ? 'true' : 'false'],
      ..._getSddEligibilityData({ selectedUpcData, selectedSddData }),
      ..._getThirdVariantLinkData({ isSplitThirdVariant, isVariant, isConsolidatedThirdVariant }),
    };

    // BCOM only
    const bcomUtagData = {
      product_bops_available: [`${!!selectedUpcData?.availability?.bopsAvailability}`],
      product_boss_available: [`${!!selectedUpcData?.availability?.bossAvailability}`],
      product_color: [`${selectedColor?.value?.name}`],
      product_days_ship: [`${selectedUpcData?.availability?.lowAvailability?.shipDays || ''}`],
      product_sdd_available: '', // TODO: To be done after store selection is implemented
      product_size: [`${selectedSize.value?.id || ''}`],
      ..._getNewArrivalAndMarkdownData(isNewFlag, selectedPrice?.value?.priceType.text),
      ..._getConsolidatedMasterData({ isConsolidatedMaster: isConsolidatedMasterValue }),

      // customer_store_id -> TODO: To be done after store selection is implemented
    };

    const utagData = {
      ...baseUtagData,
      ...(isMcomValue ? mcomUtagData : bcomUtagData),
      ...extraData,
    };

    fireLinkTag(utagData);
  };

  return {
    fireTagOnChange,
  };
}

export default useTraitAnalyticsData;
